import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { verifyTotpSchema, type TotpSetupResponse, type VerifyTotpInput } from '@bifeai/shared';
import { Button, Callout, Field, Input, Spinner } from '@bifeai/ui';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { reloadMe, useMeQuery } from '../../lib/session';
import { AuthLayout } from './AuthLayout';

/**
 * Activación obligatoria de la verificación en dos pasos (dueños).
 * Se escanea el QR con Google Authenticator, Microsoft Authenticator o similar.
 */
export function SetupTwoFactorPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMeQuery();
  const [formError, setFormError] = useState<string | null>(null);
  const setup = useMutation({
    mutationFn: () => api.post<TotpSetupResponse>('/auth/2fa/setup'),
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyTotpInput>({ resolver: zodResolver(verifyTotpSchema) });

  const needsSetup = Boolean(me.data && !me.data.user.totpEnabled);
  const started = useRef(false);
  const { mutate } = setup;
  useEffect(() => {
    // Un solo pedido aunque React monte dos veces en desarrollo.
    if (needsSetup && !started.current) {
      started.current = true;
      mutate();
    }
  }, [needsSetup, mutate]);

  if (me.isPending)
    return (
      <div className="bf-center-screen">
        <Spinner />
      </div>
    );
  if (me.isError) return <Navigate to="/ingresar" replace />;
  if (me.data.user.totpEnabled) return <Navigate to={me.data.session.mfaVerified ? '/' : '/ingresar/codigo'} replace />;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post('/auth/2fa/enable', values);
      await reloadMe(qc);
      navigate(me.data.user.mustChangePassword ? '/cambiar-clave' : '/', {
        replace: true,
      });
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <AuthLayout
      title="Activá la verificación en dos pasos"
      subtitle="Protege tu carnicería aunque alguien consiga tu contraseña. Es obligatoria para el dueño."
    >
      <div className="bf-form">
        <p className="bf-hint" style={{ fontSize: 'var(--bf-text-md)', color: 'var(--bf-ink-soft)' }}>
          1. Instalá Google Authenticator o Microsoft Authenticator en tu celular.
          <br />
          2. Escaneá este código con la app.
          <br />
          3. Escribí el código de 6 números que te muestra.
        </p>
        {setup.isPending && <Spinner label="Generando código" />}
        {setup.isError && <Callout tone="danger">{errorMessage(setup.error)}</Callout>}
        {setup.data && (
          <>
            <div className="bf-qr">
              <img src={setup.data.qrDataUrl} width={200} height={200} alt="Código QR para la app de autenticación" />
            </div>
            <div>
              <p className="bf-hint" style={{ marginBottom: 6 }}>
                Si no podés escanearlo, cargá esta clave a mano:
              </p>
              <p className="bf-secret">{setup.data.secret.match(/.{1,4}/g)?.join(' ')}</p>
            </div>
          </>
        )}
        <form className="bf-form" onSubmit={onSubmit} noValidate>
          {formError && <Callout tone="danger">{formError}</Callout>}
          <Field label="Código de la app" error={errors.code?.message}>
            <Input
              className="bf-code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              {...register('code')}
            />
          </Field>
          <Button type="submit" variant="primary" block loading={isSubmitting} disabled={!setup.data}>
            Activar y continuar
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
